import { NgModule } from '@angular/core';
import { APOLLO_OPTIONS } from 'apollo-angular';
import { ApolloClientOptions, ApolloLink, fromPromise, InMemoryCache } from '@apollo/client/core';
import { HttpLink } from 'apollo-angular/http';
import { setContext } from "@apollo/client/link/context";
import { AuthenticationService } from "./core/services/authentication.service";
import { onError } from "@apollo/client/link/error";
import { environment } from "../environments/environment";
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorStateService } from './core/services/error-state.service';
import possibleTypes from 'src/possibleTypes.json';

const uri = environment.apiUrl; // <-- add the URL of the GraphQL server here

export function createApollo(httpLink: HttpLink,
  authService: AuthenticationService,
  errorStateService: ErrorStateService,
): ApolloClientOptions<any> {
  const auth = setContext((_operation, _context) => {
    if (!authService.currentAuthData) {
      return {};
    }
    const token: string = authService.currentAuthData.accessToken;

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  });

  const errorHandler = onError((error) => {
    const ne = error.networkError as HttpErrorResponse;
    const { operationName } = error.operation;

    if (ne.status === 401 && operationName !== "RefreshToken" && operationName !== "Login") {
      return fromPromise(authService.refreshToken().toPromise())
        .map(auth => auth ? error.operation.setContext({ headers: { Authorization: `Bearer ${auth.accessToken}` } }) : null)
        .flatMap(() => error.forward(error.operation));
    }

    errorStateService.add(ne);
    // eslint-disable-next-line no-useless-return
    return; // eslint disabled because it detected empty return as obsolete but also didn't allow some paths to not return
  });

  const link = ApolloLink.from([auth, errorHandler, httpLink.create({ uri })]);

  return {
    link,
    cache: new InMemoryCache({
      typePolicies: {
        Image: {
          keyFields: ['url'],
        },
      },
      possibleTypes,
    }),
    defaultOptions: {
      mutate: {
        fetchPolicy: 'no-cache',
      },
      watchQuery: {
        fetchPolicy: 'no-cache',
      },
      query: {
        fetchPolicy: 'network-only',
      },
    },
  };
}

@NgModule({
  providers: [
    {
      provide: APOLLO_OPTIONS,
      useFactory: createApollo,
      deps: [HttpLink, AuthenticationService, ErrorStateService],
    },
  ],
})
export class GraphQLModule { }
